import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

const PresetsContext = createContext(null);
const API_URL = import.meta.env.VITE_API_URL || "https://api.endoac.ro";

const normalizePreset = (raw = {}) => ({
  id: raw.id,
  name: raw.name || "",
  description: raw.description || "",
  serviceIds: Array.isArray(raw.serviceIds) ? raw.serviceIds : [],
  order: Number(raw.order || 0),
  active: raw.active !== false,
  createdAt: raw.createdAt || null,
  updatedAt: raw.updatedAt || null,
});

const sortPresets = (list = []) =>
  [...list].sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return a.name.localeCompare(b.name);
  });

export function PresetsProvider({ children }) {
  const { token } = useAuth();
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const authHeaders = useMemo(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const fetchPresets = useCallback(async () => {
    if (!token) {
      setPresets([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/presets`, {
        headers: { ...authHeaders },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Nu s-au putut încărca preseturile");
      const list = data?.data?.presets || [];
      setPresets(sortPresets(list.map(normalizePreset)));
    } catch (err) {
      console.error("Eroare preseturi:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, token]);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  const createPreset = useCallback(
    async (payload) => {
      if (!token) return null;
      const res = await fetch(`${API_URL}/api/presets`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Nu s-a putut crea presetul");
      const preset = normalizePreset(data?.data?.preset);
      setPresets((prev) => sortPresets([preset, ...prev]));
      return preset;
    },
    [authHeaders, token]
  );

  const updatePreset = useCallback(
    async (payload) => {
      if (!payload?.id || !token) return null;
      const res = await fetch(`${API_URL}/api/presets/${payload.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Nu s-a putut actualiza presetul");
      const preset = normalizePreset(data?.data?.preset);
      setPresets((prev) => sortPresets(prev.map((p) => (p.id === preset.id ? preset : p))));
      return preset;
    },
    [authHeaders, token]
  );

  const deletePreset = useCallback(
    async (presetId) => {
      if (!presetId || !token) return false;
      const res = await fetch(`${API_URL}/api/presets/${presetId}`, {
        method: "DELETE",
        headers: { ...authHeaders },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Nu s-a putut șterge presetul");
      setPresets((prev) => prev.filter((p) => p.id !== presetId));
      return true;
    },
    [authHeaders, token]
  );

  const reorderPresets = useCallback(
    async (updates) => {
      if (!token || !Array.isArray(updates) || updates.length === 0) return false;
      const res = await fetch(`${API_URL}/api/presets/reorder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify({ updates }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Nu s-a putut rearanja lista");
      setPresets((prev) => {
        const map = new Map(prev.map((p) => [p.id, p]));
        updates.forEach((u) => {
          const existing = map.get(u.id);
          if (!existing) return;
          map.set(u.id, { ...existing, order: typeof u.order !== "undefined" ? u.order : existing.order });
        });
        return sortPresets(Array.from(map.values()));
      });
      return true;
    },
    [authHeaders, token]
  );

  const value = useMemo(
    () => ({
      presets,
      loading,
      error,
      fetchPresets,
      createPreset,
      updatePreset,
      deletePreset,
      reorderPresets,
    }),
    [presets, loading, error, fetchPresets, createPreset, updatePreset, deletePreset, reorderPresets]
  );

  return <PresetsContext.Provider value={value}>{children}</PresetsContext.Provider>;
}

export const usePresets = () => useContext(PresetsContext);
